<template>
  <div class="s4c-root">
    <div class="s4c-header">
      <div>
        <h4 class="mb-0">{{ $t('s4c-utilization.title') }}</h4>
        <p class="s4c-sub mb-0">
          {{ $t('s4c-utilization.subtitle') }}
          <span v-if="syncLabel">· {{ syncLabel }}</span>
        </p>
      </div>
      <div class="d-flex align-items-center flex-wrap" style="gap: 8px">
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="loading"
          @click="load"
        >
          {{ $t('s4c-utilization.reload') }}
        </b-button>
        <!-- Hidden for holding contacts: the endpoint 403s them anyway, so
             offering the button would only ever produce an error. -->
        <b-button
          v-if="canShare"
          size="sm"
          variant="outline-primary"
          @click="shareOpen = true"
        >
          {{ $t('s4c-utilization.share.button') }}
        </b-button>
      </div>
    </div>

    <s4c-utilization-share v-if="canShare" v-model="shareOpen" />

    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>
    <b-alert v-if="syncStale" show variant="warning" class="small">
      {{ $t('s4c-utilization.stale', { when: syncLabel }) }}
    </b-alert>

    <div v-if="loading" class="text-center py-5">
      <b-spinner />
      <span class="ml-2">{{ $t('s4c-utilization.loading') }}</span>
    </div>

    <template v-else-if="daily">
      <!-- ---------- KPI tiles ---------- -->
      <div class="s4c-kpis">
        <div v-for="t in kpis" :key="t.label" class="s4c-kpi" :class="t.cls">
          <div class="lbl">{{ t.label }}</div>
          <div class="val">{{ t.value }}</div>
          <div class="note">{{ t.note }}</div>
        </div>
      </div>

      <!-- ---------- weekly: revenue / capacity / utilization ---------- -->
      <h6 class="s4c-h">{{ $t('s4c-utilization.weekly.heading') }}</h6>
      <p class="s4c-note">{{ $t('s4c-utilization.weekly.note') }}</p>
      <weekly-panels
        v-if="weekly && weekly.weeks.length"
        :weeks="weekly.weeks"
        :sources="weekly.sources"
      />

      <!-- ---------- hero x day ---------- -->
      <!-- Internal only. Per-technician daily load is staffing detail, not a
           capacity figure; the API omits `cells` entirely for holding contacts,
           so this cannot render for them even if the guard were removed. -->
      <template v-if="showInternal">
        <h6 class="s4c-h">{{ $t('s4c-utilization.heatmap.heading') }}</h6>
        <p class="s4c-note">{{ $t('s4c-utilization.heatmap.note') }}</p>
        <hero-heatmap :days="daily.days" :roster="daily.roster" />
      </template>

      <!-- ---------- biggest free capacity ---------- -->
      <template v-if="showInternal">
        <h6 class="s4c-h">{{ $t('s4c-utilization.free.heading') }}</h6>
        <div class="s4c-tbl-scroll">
          <table class="s4c-tbl">
            <thead>
              <tr>
                <th>{{ $t('s4c-utilization.heatmap.hero') }}</th>
                <th class="num">{{ $t('s4c-utilization.free.hours') }}</th>
                <th class="num">{{ $t('s4c-utilization.free.share') }}</th>
                <th class="num">{{ $t('s4c-utilization.free.util') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in topFree" :key="r.id">
                <td>{{ r.name }}</td>
                <td class="num">{{ r.free.toFixed(0) }} h</td>
                <td class="num">{{ (100 * r.share).toFixed(0) }}%</td>
                <td class="num">
                  {{ r.util === null ? '—' : `${Math.round(100 * r.util)}%` }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- ---------- cancellations by source ---------- -->
      <!-- Internal only, and the endpoint 403s a holding caller — `cancellations`
           stays null for them, so this is belt and braces. -->
      <template v-if="showInternal && cancellations">
        <h6 class="s4c-h">{{ $t('s4c-utilization.cancel.heading') }}</h6>
        <p class="s4c-note">
          {{
            $t('s4c-utilization.cancel.note', {
              n: cancellations.n,
              share: Math.round(100 * cancellations.share),
              median: cancellations.median_lead,
            })
          }}
        </p>
        <div class="s4c-src-grid">
          <div
            v-for="s in cancellations.sources"
            :key="s.label"
            class="s4c-src"
          >
            <div class="s4c-src-head">
              <strong>{{ s.label }}</strong>
              <span>{{ Math.round(100 * s.share) }}%</span>
            </div>
            <div class="s4c-src-stats">
              <div>
                <span class="k">{{ $t('s4c-utilization.cancel.hours') }}</span>
                <span class="v">
                  {{ Math.round(s.h).toLocaleString('de-DE') }} h
                </span>
              </div>
              <div>
                <span class="k">{{ $t('s4c-utilization.cancel.count') }}</span>
                <span class="v">{{ s.n }}</span>
              </div>
              <div>
                <span class="k">{{ $t('s4c-utilization.cancel.median') }}</span>
                <span class="v">
                  {{ s.median_lead === null ? '—' : `${s.median_lead} d` }}
                </span>
              </div>
              <div>
                <span class="k">{{ $t('s4c-utilization.cancel.short') }}</span>
                <span class="v">{{ Math.round(100 * s.short_pct) }}%</span>
              </div>
            </div>
            <ul class="s4c-reasons">
              <li v-for="r in s.reasons.slice(0, 4)" :key="r.label">
                <span>{{ r.label }}</span>
                <span class="num">{{ Math.round(r.h) }} h</span>
              </li>
            </ul>
          </div>
        </div>
      </template>

      <!-- ---------- caveats ---------- -->
      <h6 class="s4c-h">{{ $t('s4c-utilization.caveats.heading') }}</h6>
      <ul class="s4c-caveats">
        <li v-for="(c, i) in caveats" :key="i">{{ c }}</li>
      </ul>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isConfinedProfileSet } from '@gid/models';
import Api from '@gid/vue-common/api';
import HeroHeatmap from '@/components/s4c-utilization/HeroHeatmap.vue';
import WeeklyPanels from '@/components/s4c-utilization/WeeklyPanels.vue';
import S4cUtilizationShare from '@/components/s4c-utilization/S4cUtilizationShare.vue';

// A mirror older than this is called out in the UI. A failed nightly sync looks
// exactly like a collapse in bookings, so staleness has to be visible next to
// the numbers rather than only in the sync table.
const STALE_HOURS = 36;

export default {
  name: 'S4cUtilization',
  components: { HeroHeatmap, WeeklyPanels, S4cUtilizationShare },
  data() {
    return {
      loading: false,
      error: null,
      shareOpen: false,
      daily: null,
      weekly: null,
      cancellations: null,
      sync: [],
    };
  },
  computed: {
    ...mapGetters(['contactProfiles']),
    canShare() {
      return !isConfinedProfileSet(this.contactProfiles);
    },
    /**
     * Two variants of this page, keyed on whether the contact is confined to a
     * holding profile.
     *
     * Internal profiles (Product Power User and the rest of the admin set) get
     * everything. A holding contact gets the investor cut: KPIs, the weekly
     * revenue/capacity/utilization panels and the caveats — without the
     * per-technician grid, the free-capacity ranking or the cancellation
     * breakdown, all of which are operational detail rather than a business
     * figure.
     *
     * The same split is enforced in gid-api, so this decides what is *rendered*,
     * not what is protected.
     */
    showInternal() {
      return !isConfinedProfileSet(this.contactProfiles);
    },
    /**
     * `roster` is an array of {id, name} for internal callers and `{count}` for
     * holding ones, because the reduced payload drops the names. The KPI tile
     * wants the size either way.
     */
    rosterCount() {
      const roster = this.daily?.roster;
      if (!roster) return 0;
      return Array.isArray(roster) ? roster.length : roster.count || 0;
    },
    lastSync() {
      const stamps = this.sync
        .filter((s) => s.ok && s.last_run_at)
        .map((s) => new Date(s.last_run_at).getTime());
      return stamps.length ? new Date(Math.min(...stamps)) : null;
    },
    syncLabel() {
      if (!this.lastSync) return null;
      return this.$t('s4c-utilization.synced', {
        when: this.$moment(this.lastSync).format('LLL'),
      });
    },
    syncStale() {
      if (!this.lastSync) return false;
      return Date.now() - this.lastSync.getTime() > STALE_HOURS * 3600 * 1000;
    },
    // IST/PLAN split runs on the ISO-week boundary, not on today: splitting on
    // today puts the current week in both halves and double-counts its capacity.
    split() {
      if (!this.daily) return null;
      const today = new Date(this.daily.today);
      const monday = new Date(today);
      monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
      return monday.toISOString().slice(0, 10);
    },
    ist() {
      if (!this.daily) return [];
      return this.daily.days.filter((d) => d.date < this.split);
    },
    plan() {
      if (!this.daily) return [];
      return this.daily.days.filter((d) => d.date >= this.split);
    },
    kpis() {
      const agg = (days) => {
        const cap = days.reduce((a, d) => a + d.cap, 0);
        const booked = days.reduce((a, d) => a + d.booked, 0);
        const free = days.reduce((a, d) => a + d.free, 0);
        const absent = days.reduce((a, d) => a + d.absent, 0);
        const jobs = days.reduce((a, d) => a + d.jobs, 0);
        return {
          cap,
          booked,
          free,
          absent,
          jobs,
          util: cap ? booked / cap : 0,
        };
      };
      const i = agg(this.ist);
      const p = agg(this.plan);
      const t = this.$t.bind(this);
      return [
        {
          label: t('s4c-utilization.kpi.ist'),
          value: `${Math.round(100 * i.util)}%`,
          note: t('s4c-utilization.kpi.jobs', { n: i.jobs }),
          cls: '',
        },
        {
          label: t('s4c-utilization.kpi.plan'),
          value: `${Math.round(100 * p.util)}%`,
          note: t('s4c-utilization.kpi.jobs', { n: p.jobs }),
          cls: 'wip',
        },
        {
          label: t('s4c-utilization.kpi.free'),
          value: `${Math.round(p.free / 60).toLocaleString('de-DE')} h`,
          note: t('s4c-utilization.kpi.mandays', {
            n: Math.round(p.free / 480),
          }),
          cls: 'wip',
        },
        {
          label: t('s4c-utilization.kpi.absence'),
          value: `${Math.round((100 * p.absent) / (p.cap + p.absent) || 0)}%`,
          note: t('s4c-utilization.kpi.absence-note'),
          cls: 'wip',
        },
        {
          label: t('s4c-utilization.kpi.roster'),
          value: String(this.rosterCount),
          note: t('s4c-utilization.kpi.roster-note'),
          cls: '',
        },
      ];
    },
    topFree() {
      // `cells` is absent on the reduced (holding) payload, so this is empty
      // there rather than throwing on a missing array.
      if (!this.daily || !this.showInternal) return [];
      const per = new Map();
      this.plan.forEach((day) => {
        (day.cells || []).forEach((c) => {
          const e = per.get(c.hero_id) || {
            id: c.hero_id,
            name: c.hero,
            free: 0,
            cap: 0,
            booked: 0,
          };
          e.free += c.free;
          e.cap += c.cap;
          e.booked += c.booked;
          per.set(c.hero_id, e);
        });
      });
      const rows = [...per.values()];
      const total = rows.reduce((a, r) => a + r.free, 0) || 1;
      return rows
        .sort((a, b) => b.free - a.free)
        .slice(0, 8)
        .map((r) => ({
          ...r,
          free: r.free / 60,
          share: r.free / total,
          util: r.cap ? r.booked / r.cap : null,
        }));
    },
    caveats() {
      return [
        this.$t('s4c-utilization.caveats.order-value'),
        this.$t('s4c-utilization.caveats.gid-precedence'),
        this.$t('s4c-utilization.caveats.partial-week'),
        this.$t('s4c-utilization.caveats.capped'),
        this.$t('s4c-utilization.caveats.holidays'),
        this.$t('s4c-utilization.caveats.plan-absence'),
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      this.cancellations = null;
      try {
        // Cancellations are internal-only and the endpoint 403s a holding
        // caller. It is therefore requested only when it will be rendered — and
        // kept out of the Promise.all regardless, because one rejected promise
        // there takes the whole page down with it: an expected 403 on a section
        // this profile does not even show would otherwise leave a holding
        // contact staring at "Not available for this profile." and nothing else.
        const [daily, weekly] = await Promise.all([
          Api.get('/api/admin/s4c/utilization'),
          Api.get('/api/admin/s4c/utilization/weekly'),
        ]);
        this.daily = daily.data;
        this.sync = daily.data.sync || [];
        this.weekly = weekly.data;

        if (this.showInternal) {
          try {
            const { data } = await Api.get(
              '/api/admin/s4c/utilization/cancellations',
            );
            this.cancellations = data;
          } catch (e) {
            // A failure here costs one section, not the report.
            this.cancellations = null;
          }
        }
      } catch (e) {
        this.error = e.response?.data?.msg || e.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.s4c-root {
  // Scoped design tokens, mirroring the partner map so the two holding-visible
  // pages read as one product.
  --s4c-ink-1: #1c1c1c;
  --s4c-ink-2: #52514e;
  --s4c-ink-3: #898781;
  --s4c-border: #e3e3df;
  --s4c-surface-1: #ffffff;
  --s4c-surface-2: #f5f5f2;

  padding-bottom: 24px;
}
</style>

<style lang="scss" scoped>
.s4c-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 16px 0 12px;
}

.s4c-sub {
  font-size: 12.5px;
  color: var(--s4c-ink-3);
}

.s4c-h {
  margin: 20px 0 4px;
  font-size: 13px;
  font-weight: 700;
  color: var(--s4c-ink-1);
}

.s4c-note {
  font-size: 11.5px;
  color: var(--s4c-ink-3);
  margin-bottom: 8px;
}

.s4c-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.s4c-kpi {
  background: var(--s4c-surface-1);
  border: 1px solid var(--s4c-border);
  border-radius: 6px;
  padding: 9px 11px;

  .lbl {
    font-size: 11px;
    color: var(--s4c-ink-3);
  }
  .val {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
  }
  .note {
    font-size: 11px;
    color: var(--s4c-ink-3);
  }
  // Forward-looking tiles are dashed so a plan figure can never be mistaken
  // for a settled one.
  &.wip {
    border-style: dashed;
    background: transparent;
  }
}

.s4c-tbl-scroll {
  overflow-x: auto;
  border: 1px solid var(--s4c-border);
  border-radius: 6px;
  background: var(--s4c-surface-1);
}

.s4c-tbl {
  width: 100%;
  font-size: 12px;
  margin: 0;

  th,
  td {
    padding: 5px 10px;
    border-bottom: 1px solid var(--s4c-border);
    white-space: nowrap;
  }
  th {
    font-size: 11px;
    color: var(--s4c-ink-3);
    font-weight: 600;
  }
  tr:last-child td {
    border-bottom: 0;
  }
  .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
}

.s4c-src-grid {
  display: grid;
  // minmax(0, 1fr) — without the 0 the tracks default to min-content and the
  // nowrap tables inside push the grid past the viewport.
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 8px;
}

.s4c-src {
  min-width: 0;
  border: 1px solid var(--s4c-border);
  border-radius: 6px;
  background: var(--s4c-surface-1);
  padding: 9px 11px;
}

.s4c-src-head {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 6px;
}

.s4c-src-stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
  font-size: 11px;

  .k {
    color: var(--s4c-ink-3);
    margin-right: 4px;
  }
  .v {
    font-weight: 600;
  }
}

.s4c-reasons {
  list-style: none;
  padding: 0;
  margin: 7px 0 0;
  font-size: 11px;
  color: var(--s4c-ink-2);

  li {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }
  .num {
    font-variant-numeric: tabular-nums;
    color: var(--s4c-ink-3);
  }
}

.s4c-caveats {
  font-size: 11.5px;
  color: var(--s4c-ink-3);
  padding-left: 18px;
  margin-bottom: 0;
}
</style>
